// =====================================================================================
// FILE: src/context/OperatorContext.jsx
// 
// Context provider for Operator Portal
// Manages: selected club, content type, view, records, and update operations
// =====================================================================================

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  OPERATOR_BASES,
  OPERATOR_CONTENT_TYPES,
  CONTENT_STATUS_OPTIONS,
  isOperatorConfigured,
} from '../config/operatorBases';
import {
  fetchOperatorRecords,
  fetchAllOperatorRecords,
  fetchAllOperatorViews,
  fetchOperatorCategories,
  updateContentStatus,
  updateLumen8Comments,
  toggleSendToWatchFolder,
  updateTakeId,
  updateBuiltAtVenue,
  updateOperatorRecord,
  batchUpdateOperatorRecords,
  createOperatorRow,
  deleteOperatorRow,
  clearAssetIngestFlag,
} from '../services/operatorAirtable';
import {
  syncOperatorStatusToSource,
  forceResyncAllToOperator,
  checkSourceRecordsExist,
} from '../services/bidirectionalSync';
import { useAuth } from './AuthContext';

// ================= CONTEXT =================

const OperatorContext = createContext(null);

// ================= POLL INTERVAL =================

const POLL_MS = 6000; // Same as club view

// Helper to get content type from URL path
const getContentTypeFromPath = () => {
  const path = window.location.pathname;
  if (path.includes('/operator/cg')) return OPERATOR_CONTENT_TYPES.CG;
  if (path.includes('/operator/ribbon')) return OPERATOR_CONTENT_TYPES.RIBBON;
  if (path.includes('/operator/tvc')) return OPERATOR_CONTENT_TYPES.TVC;
  if (path.includes('/operator/iptv')) return OPERATOR_CONTENT_TYPES.IPTV;
  if (path.includes('/operator/led')) return OPERATOR_CONTENT_TYPES.LED;
  return 'overview'; // Default to Dashboard view — not a fetchable content type
};

// ================= PROVIDER =================

export function OperatorContextProvider({ children }) {
  // Get user's club from auth context
  // userClub = user's assigned club (club staff)
  // appClub = the app's configured club from REACT_APP_DEFAULT_CLUB (for Lumen8 staff)
  const { userClub, appClub } = useAuth();
  
  // Determine default club: user's club > app's club > 'all'
  const getInitialClub = () => {
    // First try user's assigned club
    if (userClub) {
      const isValid = OPERATOR_BASES.some(base => base.id === userClub);
      if (isValid) return userClub;
    }
    // Then try app's configured club (for Lumen8 staff on club-specific deployments)
    if (appClub) {
      const isValid = OPERATOR_BASES.some(base => base.id === appClub);
      if (isValid) return appClub;
    }
    // Fallback to all
    return 'all';
  };
  
  // Selection state - initialize from user's club, app club, or 'all'
  const [selectedClub, setSelectedClub] = useState(getInitialClub);
  const [contentType, setContentType] = useState(getContentTypeFromPath);
  const [selectedView, setSelectedViewRaw] = useState(() => {
    // Initialize from shared sessionStorage so Operator ↔ MatchFlow stay in sync
    try {
      return sessionStorage.getItem('venueflow_selected_match') || null;
    } catch (e) { return null; }
  });
  
  // Wrap setSelectedView to also persist to sessionStorage
  const setSelectedView = useCallback((view) => {
    setSelectedViewRaw(view);
    try {
      if (view) {
        sessionStorage.setItem('venueflow_selected_match', view);
      } else {
        sessionStorage.removeItem('venueflow_selected_match');
      }
    } catch (e) {}
  }, []);
  
  // Resolve 'SHARED' club IDs to the actual club base for API calls.
  // Shared IPTV content is tagged _clubId='SHARED' for display but lives in
  // the same Airtable base as the default club — use appClub as the fallback.
  const resolveClubId = useCallback((clubId) => {
    if (clubId === 'SHARED') return appClub || selectedClub || 'all';
    return clubId;
  }, [appClub, selectedClub]);
  
  // Track if we've set the initial club from user
  const initialClubSetRef = useRef(false);
  
  // Update selectedClub when userClub or appClub becomes available (after auth loads)
  useEffect(() => {
    if (!initialClubSetRef.current) {
      const club = userClub || appClub;
      if (club) {
        const isValid = OPERATOR_BASES.some(base => base.id === club);
        if (isValid) {
          setSelectedClub(club);
          initialClubSetRef.current = true;
        }
      }
    }
  }, [userClub, appClub]);
  
  // Data state
  const [records, setRecords] = useState([]);
  const [views, setViews] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Stale record detection — source records that no longer exist in MatchFlow Builder
  const [staleSourceIds, setStaleSourceIds] = useState(new Set());
  
  // Optimistic update tracking
  const [pendingUpdates, setPendingUpdates] = useState(new Set());
  
  // Ref to track pending deletes (for use in loadRecords without stale closure)
  const pendingDeletesRef = useRef(new Set());
  
  // Ref to store pending optimistic field values (prevents polling from overwriting)
  // Map<recordId, { fieldName: value, ... }>
  const pendingFieldsRef = useRef(new Map());
  
  // Polling ref
  const pollRef = useRef(null);
  
  // ================= LOAD VIEWS =================
  
  const loadViews = useCallback(async () => {
    try {
      const allViews = await fetchAllOperatorViews(contentType);
      setViews(allViews);
      
      // Validate sessionStorage-initialised view exists in loaded views
      if (selectedView && allViews.length > 0) {
        const viewExists = allViews.some(v => v.name === selectedView);
        if (!viewExists) {
          console.log(`[OperatorContext] Shared match "${selectedView}" not found in ${contentType} views, clearing`);
          setSelectedView(null);
        }
      }
    } catch (err) {
      console.error('[OperatorContext] Failed to load views:', err);
      setError(err.message);
    }
  }, [contentType, selectedView, setSelectedView]);
  
  // ================= LOAD RECORDS =================
  
  const loadRecords = useCallback(async (showLoading = true) => {
    // Overview/dashboard tab loads data differently — don't call fetchOperatorRecords
    if (contentType === 'overview') {
      setLoading(false);
      return;
    }
    
    if (showLoading) setLoading(true);
    setError(null);
    
    // Validate view name before making API calls — fixture views follow patterns like
    // "RD4_NM_v_CAR_MRV", "R1_STK_v_MEL_MCG", etc.
    // Non-fixture values like "Marvel Team List" would cause 422 errors
    let validatedView = selectedView;
    if (selectedView && !/^(RD?\d|F\d|GF|EF|PF|SF|QF)/i.test(selectedView)) {
      console.log(`[OperatorContext] View "${selectedView}" doesn't match fixture pattern, skipping API filter`);
      validatedView = null;
      setSelectedView(null);
    }
    
    try {
      let data;
      
      if (selectedClub === 'all') {
        // Fetch from all clubs
        data = await fetchAllOperatorRecords(contentType, validatedView);
      } else {
        // Fetch from single club
        data = await fetchOperatorRecords(selectedClub, contentType, validatedView);
      }
      
      // Filter out records that are pending deletion (prevents flicker during polling)
      const filtered = data.filter(r => !pendingDeletesRef.current.has(r.id));
      
      // Merge: preserve pending optimistic field values
      const merged = filtered.map(r => {
        const pendingFields = pendingFieldsRef.current.get(r.id);
        if (pendingFields) {
          console.log('[OperatorContext] Preserving pending fields for:', r.id, pendingFields);
          // Merge pending optimistic values over server values
          return {
            ...r,
            fields: {
              ...r.fields,
              ...pendingFields,
            },
          };
        }
        return r;
      });
      
      setRecords(merged);
    } catch (err) {
      console.error('[OperatorContext] Failed to load records:', err);
      setError(err.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [selectedClub, contentType, selectedView, setSelectedView]);
  
  // ================= LOAD CATEGORIES =================
  
  const loadCategories = useCallback(async () => {
    if (!selectedClub) return;
    try {
      let cats = [];
      if (selectedClub === 'all') {
        // When 'all' clubs, fetch categories from first available club
        const firstClub = OPERATOR_BASES[0];
        if (firstClub) {
          cats = await fetchOperatorCategories(firstClub.id, contentType);
        }
      } else {
        cats = await fetchOperatorCategories(selectedClub, contentType);
      }
      console.log(`[OperatorContext] Loaded ${cats.length} categories for ${contentType}:`, cats);
      setAllCategories(cats);
    } catch (err) {
      console.warn('[OperatorContext] Failed to load categories:', err);
    }
  }, [selectedClub, contentType]);
  
  // ================= POLLING =================
  
  useEffect(() => {
    // Initial load
    loadViews();
    loadCategories();
  }, [contentType, selectedClub, loadViews, loadCategories]); // Reload when content type OR club changes
  
  useEffect(() => {
    // Load records when selection changes
    if (selectedView) {
      loadRecords();
    }
  }, [selectedClub, contentType, selectedView, loadRecords]);
  
  // ── Stale record detection ──
  // After records load, check which Source Record IDs no longer exist in the builder.
  // Runs once when records change (not on every poll to save API calls).
  const lastStaleCheckRef = useRef('');
  useEffect(() => {
    if (records.length === 0) { setStaleSourceIds(new Set()); return; }
    
    // Build a fingerprint of current source IDs to avoid re-checking on poll refreshes
    const sourceIds = records
      .map(r => r.fields?.['Source Record ID'])
      .filter(Boolean);
    const fingerprint = [...sourceIds].sort().join(',');
    if (fingerprint === lastStaleCheckRef.current) return;
    lastStaleCheckRef.current = fingerprint;
    
    if (sourceIds.length === 0) { setStaleSourceIds(new Set()); return; }

    // Run the check in background — don't block UI
    (async () => {
      try {
        const stale = await checkSourceRecordsExist(sourceIds);
        setStaleSourceIds(stale);
      } catch (err) {
        console.warn('[OperatorContext] Stale check failed:', err);
      }
    })();
  }, [records]);
  
  useEffect(() => {
    // Set up polling
    if (pollRef.current) {
      clearInterval(pollRef.current);
    }
    
    pollRef.current = setInterval(() => {
      if (selectedView) {
        loadRecords(false); // Silent refresh
      }
    }, POLL_MS);
    
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [selectedView, loadRecords]);
  
  // ================= UPDATE HANDLERS =================
  
  /**
   * Update content status for a record
   */
  const handleUpdateStatus = useCallback(async (record, newStatus) => {
    const recordId = record.id;
    const clubId = resolveClubId(record._clubId);
    const type = record._contentType;
    
    console.log('[OperatorContext] Status update START:', { recordId, clubId, type, newStatus });
    
    // Store optimistic value in ref (prevents polling from overwriting)
    const existing = pendingFieldsRef.current.get(recordId) || {};
    pendingFieldsRef.current.set(recordId, { ...existing, 'Content Status': newStatus });
    
    // Optimistic update
    setPendingUpdates(prev => new Set([...prev, recordId]));
    setRecords(prev => prev.map(r => 
      r.id === recordId 
        ? { ...r, fields: { ...r.fields, 'Content Status': newStatus } }
        : r
    ));
    
    try {
      console.log('[OperatorContext] Calling updateContentStatus API...');
      await updateContentStatus(clubId, type, recordId, newStatus);
      console.log('[OperatorContext] Status update SUCCESS');
      
      // ✅ Direction 2: Sync operator status back to source record (non-blocking)
      const sourceRecordId = record.fields?.['Source Record ID'];
      if (sourceRecordId) {
        // Pass clubId so Direction 2 uses the correct club's operator tables
        syncOperatorStatusToSource(sourceRecordId, newStatus, { clubId }).catch(err => {
          console.warn('[OperatorContext] Direction 2 status sync failed (non-blocking):', err);
        });
      } else {
        console.warn('[OperatorContext] No Source Record ID on record - cannot sync to MatchFlow');
      }
    } catch (err) {
      console.error('[OperatorContext] Failed to update status:', err);
      // Revert on error - remove from ref first so loadRecords can overwrite
      pendingFieldsRef.current.delete(recordId);
      loadRecords(false);
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
      // Remove from ref after a longer delay to ensure server has updated and polling has fresh data
      setTimeout(() => {
        const current = pendingFieldsRef.current.get(recordId);
        if (current) {
          delete current['Content Status'];
          if (Object.keys(current).length === 0) {
            pendingFieldsRef.current.delete(recordId);
          }
          console.log('[OperatorContext] Status protection cleared for:', recordId);
        }
      }, 8000); // Increased from 3000 to 8000ms
    }
  }, [loadRecords, resolveClubId]);
  
  /**
   * Update Lumen8 comments for a record
   */
  const handleUpdateComments = useCallback(async (record, comments) => {
    const recordId = record.id;
    const clubId = resolveClubId(record._clubId);
    const type = record._contentType;
    
    // Store optimistic value in ref (prevents polling from overwriting)
    const existing = pendingFieldsRef.current.get(recordId) || {};
    pendingFieldsRef.current.set(recordId, { ...existing, 'Lumen8 Comments': comments });
    
    // Optimistic update
    setPendingUpdates(prev => new Set([...prev, recordId]));
    setRecords(prev => prev.map(r => 
      r.id === recordId 
        ? { ...r, fields: { ...r.fields, 'Lumen8 Comments': comments } }
        : r
    ));
    
    try {
      await updateLumen8Comments(clubId, type, recordId, comments);
      
      // ✅ Direction 2: Sync merged comments back to source record (non-blocking)
      const sourceRecordId = record.fields?.['Source Record ID'];
      if (sourceRecordId) {
        // Use current status for the sync (comments-only sync still evaluates sibling statuses)
        const currentStatus = record.fields?.['Content Status'] || '';
        syncOperatorStatusToSource(sourceRecordId, currentStatus, { clubId }).catch(err => {
          console.warn('[OperatorContext] Direction 2 comment sync failed (non-blocking):', err);
        });
      }
    } catch (err) {
      console.error('[OperatorContext] Failed to update comments:', err);
      pendingFieldsRef.current.delete(recordId);
      loadRecords(false);
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
      setTimeout(() => {
        const current = pendingFieldsRef.current.get(recordId);
        if (current) {
          delete current['Lumen8 Comments'];
          if (Object.keys(current).length === 0) {
            pendingFieldsRef.current.delete(recordId);
          }
        }
      }, 10000);
    }
  }, [loadRecords, resolveClubId]);
  
  /**
   * Toggle "Send To Watch Folder" checkbox
   */
  const handleToggleWatchFolder = useCallback(async (record, value) => {
    const recordId = record.id;
    const clubId = resolveClubId(record._clubId);
    const type = record._contentType;
    
    // When toggling ON (Check & Ingest), also ensure Content Status is NOT "Approved by Lumen8"
    // so the MatchFlow row stays unlocked for clubs. Lock only happens at Built step.
    const resetStatus = value ? 'Ready for Testing' : null;
    
    // Store optimistic value in ref (prevents polling from overwriting)
    const existing = pendingFieldsRef.current.get(recordId) || {};
    pendingFieldsRef.current.set(recordId, { 
      ...existing, 
      'Send To Watch Folder': value,
      ...(resetStatus ? { 'Content Status': resetStatus } : {}),
    });
    
    // Optimistic update
    setPendingUpdates(prev => new Set([...prev, recordId]));
    setRecords(prev => prev.map(r => 
      r.id === recordId 
        ? { ...r, fields: { 
            ...r.fields, 
            'Send To Watch Folder': value,
            ...(resetStatus ? { 'Content Status': resetStatus } : {}),
          } }
        : r
    ));
    
    try {
      await toggleSendToWatchFolder(clubId, type, recordId, value);
      
      // Also reset Content Status to ensure row is unlocked for clubs
      if (resetStatus) {
        await updateContentStatus(clubId, type, recordId, resetStatus);
        
        // Direction 2: Sync unlocked status back to MatchFlow source
        const sourceRecordId = record.fields?.['Source Record ID'];
        if (sourceRecordId) {
          syncOperatorStatusToSource(sourceRecordId, resetStatus, { clubId }).catch(err => {
            console.warn('[OperatorContext] Direction 2 check+ingest unlock sync failed (non-blocking):', err);
          });
        }
      }
    } catch (err) {
      console.error('[OperatorContext] Failed to toggle watch folder:', err);
      pendingFieldsRef.current.delete(recordId);
      loadRecords(false);
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
      setTimeout(() => {
        const current = pendingFieldsRef.current.get(recordId);
        if (current) {
          delete current['Send To Watch Folder'];
          if (resetStatus) delete current['Content Status'];
          if (Object.keys(current).length === 0) {
            pendingFieldsRef.current.delete(recordId);
          }
        }
      }, 10000);
    }
  }, [loadRecords, resolveClubId]);
  
  /**
   * Force resend content to watch folder.
   * 1. Clear the MCG/Marvel Ingested flag on the asset record
   * 2. Toggle Send To Watch Folder off then on to re-trigger the Airtable automation
   */
  const handleForceResend = useCallback(async (record) => {
    const recordId = record.id;
    const clubId = resolveClubId(record._clubId);
    const type = record._contentType;
    const baseId = record._baseId;
    const assetId = record.fields?.['_assetId'];
    const venue = record.fields?.['Venue'] || record.fields?.['Destination Venue'] || '';
    
    if (!assetId) {
      alert('Cannot force resend — no asset linked to this row.');
      return;
    }
    
    if (!window.confirm('Force resend this content to the watch folder?\n\nThis will clear the ingest flag and re-trigger delivery to venue.')) {
      return;
    }
    
    setPendingUpdates(prev => new Set([...prev, recordId]));
    
    try {
      // Step 1: Clear the ingest flag on the asset record
      await clearAssetIngestFlag(baseId, assetId, venue);
      
      // Step 2: Toggle watch folder off
      await toggleSendToWatchFolder(clubId, type, recordId, false);
      
      // Step 3: Brief delay for Airtable to register the change
      await new Promise(r => setTimeout(r, 1500));
      
      // Step 4: Toggle watch folder back on — triggers the automation
      await toggleSendToWatchFolder(clubId, type, recordId, true);
      
      console.log(`[OperatorContext] Force resend complete for ${recordId} (asset ${assetId})`);
      
      // Optimistic: update local state to show as sent
      setRecords(prev => prev.map(r => 
        r.id === recordId 
          ? { ...r, fields: { ...r.fields, 'Send To Watch Folder': true, '_mcgIngest': false, '_marvelIngest': false } }
          : r
      ));
    } catch (err) {
      console.error('[OperatorContext] Force resend failed:', err);
      alert(`Force resend failed: ${err.message}`);
      loadRecords(false);
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
    }
  }, [loadRecords, resolveClubId]);
  
  /**
   * Update Take ID for a record
   */
  const handleUpdateTakeId = useCallback(async (record, takeIdValue) => {
    const recordId = record.id;
    const clubId = resolveClubId(record._clubId);
    const type = record._contentType;
    
    // Store optimistic value in ref (prevents polling from overwriting)
    const existing = pendingFieldsRef.current.get(recordId) || {};
    pendingFieldsRef.current.set(recordId, { ...existing, 'Take ID': takeIdValue });
    
    // Optimistic update
    setPendingUpdates(prev => new Set([...prev, recordId]));
    setRecords(prev => prev.map(r => 
      r.id === recordId 
        ? { ...r, fields: { ...r.fields, 'Take ID': takeIdValue } }
        : r
    ));
    
    try {
      await updateTakeId(clubId, type, recordId, takeIdValue);
    } catch (err) {
      console.error('[OperatorContext] Failed to update Take ID:', err);
      pendingFieldsRef.current.delete(recordId);
      loadRecords(false);
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
      setTimeout(() => {
        const current = pendingFieldsRef.current.get(recordId);
        if (current) {
          delete current['Take ID'];
          if (Object.keys(current).length === 0) {
            pendingFieldsRef.current.delete(recordId);
          }
        }
      }, 10000);
    }
  }, [loadRecords, resolveClubId]);
  
  /**
   * Toggle "Built at Venue" checkbox + auto-stamp timestamp
   */
  const handleToggleBuiltAtVenue = useCallback(async (record, checked) => {
    const recordId = record.id;
    const clubId = resolveClubId(record._clubId);
    const type = record._contentType;
    const timestamp = checked ? new Date().toISOString() : '';
    
    // When marking as Built, also set Content Status to "Approved by Lumen8"
    // to lock the MatchFlow row so clubs can't edit it.
    const statusUpdate = checked ? 'Approved by Lumen8' : (record.fields?.['Content Status'] || '');
    
    // Store optimistic value in ref (prevents polling from overwriting)
    const existing = pendingFieldsRef.current.get(recordId) || {};
    pendingFieldsRef.current.set(recordId, { 
      ...existing, 
      'Built at Venue': checked,
      'Built at Venue Time': timestamp,
      ...(checked ? { 'Content Status': statusUpdate } : {}),
    });
    
    // Optimistic update
    setPendingUpdates(prev => new Set([...prev, recordId]));
    setRecords(prev => prev.map(r => 
      r.id === recordId 
        ? { ...r, fields: { ...r.fields, 'Built at Venue': checked, 'Built at Venue Time': timestamp, ...(checked ? { 'Content Status': statusUpdate } : {}) } }
        : r
    ));
    
    try {
      await updateBuiltAtVenue(clubId, type, recordId, checked);
      
      // Also update Content Status to lock the row when built
      if (checked) {
        await updateContentStatus(clubId, type, recordId, statusUpdate);
        
        // Direction 2: Sync lock status back to MatchFlow source
        const sourceRecordId = record.fields?.['Source Record ID'];
        if (sourceRecordId) {
          syncOperatorStatusToSource(sourceRecordId, statusUpdate, { clubId }).catch(err => {
            console.warn('[OperatorContext] Direction 2 built+lock sync failed (non-blocking):', err);
          });
        }
      }
    } catch (err) {
      console.error('[OperatorContext] Failed to toggle Built at Venue:', err);
      pendingFieldsRef.current.delete(recordId);
      loadRecords(false);
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
      setTimeout(() => {
        const current = pendingFieldsRef.current.get(recordId);
        if (current) {
          delete current['Built at Venue'];
          delete current['Built at Venue Time'];
          if (checked) delete current['Content Status'];
          if (Object.keys(current).length === 0) {
            pendingFieldsRef.current.delete(recordId);
          }
        }
      }, 10000);
    }
  }, [loadRecords, resolveClubId]);
  
  /**
   * Batch update status for multiple records
   */
  const handleBatchUpdateStatus = useCallback(async (recordsToUpdate, newStatus) => {
    // Group by club
    const byClub = {};
    recordsToUpdate.forEach(r => {
      const cid = resolveClubId(r._clubId);
      if (!byClub[cid]) byClub[cid] = [];
      byClub[cid].push({
        id: r.id,
        fields: { 'Content Status': newStatus },
      });
    });
    
    // Store optimistic values in ref (prevents polling from overwriting)
    const ids = new Set(recordsToUpdate.map(r => r.id));
    ids.forEach(id => {
      const existing = pendingFieldsRef.current.get(id) || {};
      pendingFieldsRef.current.set(id, { ...existing, 'Content Status': newStatus });
    });
    
    // Optimistic update
    setPendingUpdates(prev => new Set([...prev, ...ids]));
    setRecords(prev => prev.map(r => 
      ids.has(r.id) 
        ? { ...r, fields: { ...r.fields, 'Content Status': newStatus } }
        : r
    ));
    
    try {
      for (const [clubId, updates] of Object.entries(byClub)) {
        const type = recordsToUpdate.find(r => resolveClubId(r._clubId) === clubId)?._contentType;
        await batchUpdateOperatorRecords(clubId, type, updates);
      }
      
      // ✅ Direction 2: Sync batch status changes back to source records (non-blocking)
      // Group by source record ID AND club to pass correct clubId
      const sourcesByClub = new Map();
      recordsToUpdate.forEach(r => {
        const sourceId = r.fields?.['Source Record ID'];
        const clubId = resolveClubId(r._clubId);
        if (sourceId && clubId) {
          if (!sourcesByClub.has(sourceId)) {
            sourcesByClub.set(sourceId, clubId);
          }
        }
      });
      
      for (const [sourceRecordId, clubId] of sourcesByClub.entries()) {
        syncOperatorStatusToSource(sourceRecordId, newStatus, { clubId }).catch(err => {
          console.warn('[OperatorContext] Direction 2 batch sync failed for', sourceRecordId, err);
        });
      }
    } catch (err) {
      console.error('[OperatorContext] Failed to batch update:', err);
      ids.forEach(id => pendingFieldsRef.current.delete(id));
      loadRecords(false);
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      setTimeout(() => {
        ids.forEach(id => {
          const current = pendingFieldsRef.current.get(id);
          if (current) {
            delete current['Content Status'];
            if (Object.keys(current).length === 0) {
              pendingFieldsRef.current.delete(id);
            }
          }
        });
      }, 10000);
    }
  }, [loadRecords, resolveClubId]);
  
  /**
   * Create a new row in the operator table
   */
  const handleCreateRow = useCallback(async (clubId, fields) => {
    console.log('[OperatorContext] Creating new row:', { clubId, contentType, fields });
    
    try {
      const newRecord = await createOperatorRow(clubId, contentType, fields);
      
      // Add to local state immediately
      setRecords(prev => [...prev, newRecord]);
      
      return newRecord;
    } catch (err) {
      console.error('[OperatorContext] Failed to create row:', err);
      throw err;
    }
  }, [contentType]);
  
  // Delete a row
  const handleDeleteRow = useCallback(async (clubId, recordId) => {
    console.log('[OperatorContext] Deleting row:', { clubId, contentType, recordId });
    
    // Add to pending deletes immediately (prevents polling from bringing it back)
    pendingDeletesRef.current.add(recordId);
    
    // Remove from local state immediately for optimistic UI
    setRecords(prev => prev.filter(r => r.id !== recordId));
    
    try {
      await deleteOperatorRow(clubId, contentType, recordId);
      console.log('[OperatorContext] Successfully deleted:', recordId);
      return true;
    } catch (err) {
      console.error('[OperatorContext] Failed to delete row:', err);
      // Remove from pending deletes on error (allow it to come back)
      pendingDeletesRef.current.delete(recordId);
      throw err;
    } finally {
      // Keep in pendingDeletes for a bit longer to ensure polling doesn't bring it back
      setTimeout(() => {
        pendingDeletesRef.current.delete(recordId);
      }, 5000); // Keep blocked from polling for 5 seconds
    }
  }, [contentType]);
  
  // ================= COMPUTED VALUES =================
  
  // Group records by category
  const recordsByCategory = React.useMemo(() => {
    const grouped = {};
    records.forEach(r => {
      const category = r.fields?.Category || 'Uncategorized';
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(r);
    });
    
    // Sort each category by Cue ID
    Object.keys(grouped).forEach(cat => {
      grouped[cat].sort((a, b) => {
        const cueA = parseFloat(a.fields?.['Cue ID']) || 0;
        const cueB = parseFloat(b.fields?.['Cue ID']) || 0;
        return cueA - cueB;
      });
    });
    
    return grouped;
  }, [records]);
  
  // Filter views by selected club
  const filteredViews = React.useMemo(() => {
    if (selectedClub === 'all') return views;
    return views.filter(v => v.clubId === selectedClub);
  }, [views, selectedClub]);
  
  // Available clubs
  const clubs = React.useMemo(() => {
    return [
      { id: 'all', name: 'All Clubs', shortName: 'ALL' },
      ...OPERATOR_BASES,
    ];
  }, []);
  
  // ================= CONTEXT VALUE =================
  
  /**
   * Dedicated Unlock handler — resets ALL pipeline fields in one coordinated operation.
   * Uses extended polling protection (12s) to prevent race conditions with 6s poll cycle.
   */
  const handleUnlockRow = useCallback(async (record) => {
    const recordId = record.id;
    const clubId = resolveClubId(record._clubId);
    const type = record._contentType;
    
    console.log('[OperatorContext] Unlock START:', recordId);
    
    // Target state after unlock
    const unlockFields = {
      'Content Status': 'Ready for Testing',
      'Send To Watch Folder': false,
      'Built at Venue': false,
    };
    
    // 1. Lock fields in pendingFieldsRef FIRST — blocks polling from overwriting
    const existing = pendingFieldsRef.current.get(recordId) || {};
    pendingFieldsRef.current.set(recordId, { 
      ...existing, 
      ...unlockFields,
      'Built at Venue Time': '',
    });
    
    // 2. Single optimistic update — all fields at once
    setPendingUpdates(prev => new Set([...prev, recordId]));
    setRecords(prev => prev.map(r => 
      r.id === recordId 
        ? { ...r, fields: { 
            ...r.fields, 
            ...unlockFields,
            'Built at Venue Time': '',
          } }
        : r
    ));
    
    // 3. Write to Airtable — two calls to avoid date field issues
    // (Built at Venue Time may reject empty string in a single PATCH with other fields)
    try {
      // First: clear the checkbox + status (these are safe types: checkbox, single select)
      await updateOperatorRecord(clubId, type, recordId, unlockFields);
      console.log('[OperatorContext] Unlock: status + checkboxes cleared');
      
      // Second: clear the timestamp separately (date field — use dedicated function)
      await updateBuiltAtVenue(clubId, type, recordId, false);
      console.log('[OperatorContext] Unlock: built timestamp cleared');
      
      // Direction 2: Sync unlocked status back to MatchFlow source
      // Must send 'Content Unlocked' (not 'Ready for Testing') so the builder
      // reflects the unlocked state — 'Ready for Testing' is not handled by Direction 2
      const sourceRecordId = record.fields?.['Source Record ID'];
      if (sourceRecordId) {
        syncOperatorStatusToSource(sourceRecordId, 'Content Unlocked', { clubId }).catch(err => {
          console.warn('[OperatorContext] Direction 2 unlock sync failed (non-blocking):', err);
        });
      }
      
      console.log('[OperatorContext] Unlock SUCCESS:', recordId);
    } catch (err) {
      console.error('[OperatorContext] Unlock FAILED:', err);
      console.error('[OperatorContext] Unlock error detail:', err?.message, err?.status, JSON.stringify(err));
      // Don't delete protection on error — keep optimistic state until next manual action
      // Just force a reload after a delay to get server truth
      setTimeout(() => {
        pendingFieldsRef.current.delete(recordId);
        loadRecords(false);
      }, 2000);
    } finally {
      setPendingUpdates(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
      // Extended protection — must survive at least 2 polling cycles (12s > 2 × 6s)
      setTimeout(() => {
        const current = pendingFieldsRef.current.get(recordId);
        if (current) {
          delete current['Content Status'];
          delete current['Send To Watch Folder'];
          delete current['Built at Venue'];
          delete current['Built at Venue Time'];
          if (Object.keys(current).length === 0) {
            pendingFieldsRef.current.delete(recordId);
          }
          console.log('[OperatorContext] Unlock protection cleared — reloading fresh data');
          loadRecords(false); // Force reload after protection clears to confirm server state
        }
      }, 12000);
    }
  }, [loadRecords, resolveClubId]);

  const value = {
    // Configuration
    isConfigured: isOperatorConfigured(),
    clubs,
    contentTypes: OPERATOR_CONTENT_TYPES,
    statusOptions: CONTENT_STATUS_OPTIONS,
    
    // Selection
    selectedClub,
    setSelectedClub,
    contentType,
    setContentType,
    selectedView,
    setSelectedView,
    
    // Data
    records,
    recordsByCategory,
    allCategories,
    views: filteredViews,
    allViews: views,
    loading,
    error,
    pendingUpdates,
    staleSourceIds,
    
    // Actions
    refresh: loadRecords,
    updateStatus: handleUpdateStatus,
    updateComments: handleUpdateComments,
    toggleWatchFolder: handleToggleWatchFolder,
    forceResend: handleForceResend,
    updateTakeId: handleUpdateTakeId,
    toggleBuiltAtVenue: handleToggleBuiltAtVenue,
    unlockRow: handleUnlockRow,
    forceResyncAll: forceResyncAllToOperator,
    batchUpdateStatus: handleBatchUpdateStatus,
    createRow: handleCreateRow,
    deleteRow: handleDeleteRow,
  };
  
  return (
    <OperatorContext.Provider value={value}>
      {children}
    </OperatorContext.Provider>
  );
}

// ================= HOOK =================

export function useOperator() {
  const context = useContext(OperatorContext);
  if (!context) {
    throw new Error('useOperator must be used within an OperatorContextProvider');
  }
  return context;
}

export default OperatorContext;
